import { RefObject, useEffect } from "react";

import { OUTCOMES_TOUR_AREA_POPUP_SELECTOR, OUTCOMES_TOUR_TAP_SLOP_PX } from "consts";

export interface UseStepAwayOnOutsidePressOptions {
  cardRef: RefObject<HTMLElement>;
  target: HTMLElement | null;
  isOnBand: (x: number, y: number) => boolean;
  onStepAway: () => void;
}

// A press on a scrollbar lands on the scrolling element itself, beyond its client area.
const isOnScrollbar = (element: Element, x: number, y: number) => {
  if (element === document.documentElement) return x >= element.clientWidth || y >= element.clientHeight;
  if (!(element instanceof HTMLElement) || (element.offsetWidth <= element.clientWidth && element.offsetHeight <= element.clientHeight)) return false;
  const rect = element.getBoundingClientRect();
  return x >= rect.left + element.clientLeft + element.clientWidth || y >= rect.top + element.clientTop + element.clientHeight;
};

/** A primary press outside the card and area steps away. Touch waits for the lift, so a scroll gesture does not. */
export const useStepAwayOnOutsidePress = ({ cardRef, target, isOnBand, onStepAway }: UseStepAwayOnOutsidePressOptions) => {
  useEffect(() => {
    let touchStart: { x: number; y: number } | null = null;
    const isInsideTour = (element: Element, x: number, y: number) =>
      Boolean(cardRef.current?.contains(element) || element.closest(OUTCOMES_TOUR_AREA_POPUP_SELECTOR) || target?.contains(element)) || isOnBand(x, y);
    const handlePointerDown = (event: PointerEvent) => {
      touchStart = null;
      const pressed = event.target;
      if (event.button > 0 || !(pressed instanceof Element)) return;
      if (isInsideTour(pressed, event.clientX, event.clientY) || isOnScrollbar(pressed, event.clientX, event.clientY)) return;
      if (event.pointerType === "mouse" || !event.pointerType) onStepAway();
      else touchStart = { x: event.clientX, y: event.clientY };
    };
    const handlePointerUp = (event: PointerEvent) => {
      if (!touchStart) return;
      const moved = Math.hypot(event.clientX - touchStart.x, event.clientY - touchStart.y);
      touchStart = null;
      if (moved <= OUTCOMES_TOUR_TAP_SLOP_PX) onStepAway();
    };
    const handlePointerCancel = () => {
      touchStart = null;
    };
    document.addEventListener("pointerdown", handlePointerDown, true);
    document.addEventListener("pointerup", handlePointerUp, true);
    document.addEventListener("pointercancel", handlePointerCancel, true);
    return () => {
      document.removeEventListener("pointerdown", handlePointerDown, true);
      document.removeEventListener("pointerup", handlePointerUp, true);
      document.removeEventListener("pointercancel", handlePointerCancel, true);
    };
  }, [cardRef, target, isOnBand, onStepAway]);
};
